import React, { useState, useEffect } from 'react';
import { V } from '../../utils/theme';
import { Card } from '../../components/ui';
import { apiPost } from '../../utils/api';
import { IRON_RANKS } from '../../data/ranks';
import { useLayout } from '../../utils/responsive';
// Shared with the admin panel's user detail (tabs/features/profile.jsx) so the two
// screens can never disagree about what a member's gym sees.
import { OrgContextCard } from './orgs/OrgContextCard';

export function AdminUserAudit({s}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  const af=async(action,extra={})=>{
    try{return await apiPost("/api/admin",{action,...extra},{email});}
    catch(e){return null;}
  };

  const [users,setUsers]=useState(null);
  const [loading,setLoading]=useState(false);
  const [search,setSearch]=useState("");
  const [selUser,setSelUser]=useState(null);
  const [detail,setDetail]=useState(null);
  const [gam,setGam]=useState(null);
  const [detailLoading,setDetailLoading]=useState(false);
  const [detailError,setDetailError]=useState(null);
  const [auditTab,setAuditTab]=useState("overview");
  // Signed photo URLs, fetched on demand: user_detail no longer carries raw photo
  // URLs (they were readable by every admin), so the grid asks sign_user_photos,
  // which only the super-admin may call.
  const [photoUrls,setPhotoUrls]=useState(null);
  const [photoNote,setPhotoNote]=useState(null);

  const loadUsers=()=>{setLoading(true);af("users").then(r=>{setUsers(r?.users||[]);setLoading(false);});};

  const selectUser=(u)=>{
    setSelUser(u);setDetail(null);setGam(null);setAuditTab("overview");setDetailLoading(true);setDetailError(null);
    setPhotoUrls(null);setPhotoNote(null);
    Promise.all([af("user_detail",{email:u.email}),af("get_gamification",{email:u.email})]).then(([d,g])=>{
      // af() swallows errors and returns null — surface that instead of rendering an empty panel
      if(!d&&!g)setDetailError("Failed to load user — check session and try again");
      setDetail(d);setGam(g?.gamification||null);setDetailLoading(false);
    });
  };

  useEffect(()=>{
    if(auditTab!=="photos"||!selUser||photoUrls!==null)return;
    const ids=(detail?.photos||[]).map(p=>p.photo_id).filter(Boolean);
    if(!ids.length){setPhotoUrls({});return;}
    let cancelled=false;
    apiPost("/api/admin",{action:"sign_user_photos",email:selUser.email,photoIds:ids},{email}).catch(()=>null).then(r=>{
      if(cancelled)return;
      setPhotoUrls(r?.urls||{});
      // Any error becomes null; for this action that is almost always the 403 a
      // non-super admin gets.
      setPhotoNote(!r?"Photo previews are available to the super-admin only":r.skipped?"Photo storage is not configured on the server":null);
    });
    return()=>{cancelled=true;};
  },[auditTab,selUser,detail,photoUrls,email]);

  const filtered=(users||[]).filter(u=>!search||u.name?.toLowerCase().includes(search.toLowerCase())||u.email?.toLowerCase().includes(search.toLowerCase()));

  const TABS=[
    {id:"overview",icon:"👤",label:"Profile"},
    {id:"workouts",icon:"🏋️",label:"Workouts"},
    {id:"nutrition",icon:"🥗",label:"Nutrition"},
    {id:"body",icon:"📏",label:"Body"},
    {id:"checkins",icon:"✅",label:"Check-ins"},
    {id:"photos",icon:"📸",label:"Photos"},
    {id:"social",icon:"👥",label:"Social"},
    {id:"gamification",icon:"⚡",label:"XP"},
    {id:"activity",icon:"📋",label:"Activity Log"},
    {id:"settings",icon:"⚙️",label:"Settings"},
    {id:"gym",icon:"🏢",label:"Gym"},
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>🔎 User Audit</div>
        <div style={{fontSize:11,color:V.text3}}>Full read-only view of any user's app — exactly what they see</div>
      </div>

      {/* User selector */}
      <Card style={{padding:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text}}>Select User</div>
          {users===null&&<button onClick={loadUsers} style={{padding:"5px 12px",borderRadius:8,background:V.accent,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,color:V.bg,fontFamily:V.font}}>{loading?"Loading…":"Load Users"}</button>}
        </div>
        {users!==null&&(
          <>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name or email…"
              style={{width:"100%",padding:"8px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font,boxSizing:"border-box",marginBottom:6}}/>
            <div style={{maxHeight:160,overflowY:"auto",display:"flex",flexDirection:"column",gap:3}}>
              {filtered.map(u=>{
                const sel=selUser?.email===u.email;
                return(
                  <div key={u.email} onClick={()=>selectUser(u)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",
                    borderRadius:8,cursor:"pointer",border:`1px solid ${sel?V.accent+"40":V.cardBorder}`,
                    background:sel?`${V.accent}10`:V.card}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:sel?700:400,color:sel?V.text:V.text3}}>{u.name}</div>
                      <div style={{fontSize:9,color:V.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email} · {u.workouts} workouts · {u.photos} photos</div>
                    </div>
                    {u.isAdmin&&<span style={{fontSize:8,color:V.danger,fontWeight:700}}>ADMIN</span>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {/* Detail pane */}
      {selUser&&(
        <>
          {detailLoading&&<Card style={{padding:24,textAlign:"center"}}><div style={{fontSize:12,color:V.text3}}>Loading full profile…</div></Card>}
          {!detailLoading&&detailError&&(
            <Card style={{padding:14,borderColor:V.danger}}>
              <div style={{fontSize:12,color:V.danger,fontWeight:700,marginBottom:6}}>Could not load user</div>
              <div style={{fontSize:11,color:V.text3}}>{detailError}</div>
            </Card>
          )}
          {!detailLoading&&detail&&(
            <>
              {/* User header */}
              <Card style={{padding:14,background:`linear-gradient(135deg,${V.card},${V.card})`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:800,color:V.text}}>{detail.profile?.first_name} {detail.profile?.last_name}</div>
                    {detail.profile?.username&&<div style={{fontSize:10,color:V.accent}}>@{detail.profile.username}</div>}
                    <div style={{fontSize:9,color:V.text3,fontFamily:V.mono}}>{selUser.email}</div>
                    <div style={{fontSize:9,color:V.text3,marginTop:2}}>Joined {detail.profile?.created_at?.slice(0,10)} · Level: {detail.profile?.fitness_level||"—"} · {detail.profile?.units||"lbs"}</div>
                  </div>
                  {detail.profile?.is_banned&&<span style={{padding:"3px 8px",borderRadius:6,background:V.danger+"15",fontSize:9,fontWeight:700,color:V.danger}}>BANNED</span>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4}}>
                  {[
                    {l:"Workouts",v:detail.workouts?.length||0,c:V.accent},
                    {l:"Nutrition",v:detail.nutrition?.length||0,c:V.warn},
                    {l:"Photos",v:detail.photos?.length||0,c:V.accent2},
                    {l:"Friends",v:detail.friends?.length||0,c:"#06b6d4"},
                    {l:"Check-ins",v:detail.checkins?.length||0,c:"#ec4899"},
                  ].map(st=>(
                    <div key={st.l} style={{textAlign:"center",padding:6,borderRadius:8,background:V.card}}>
                      <div style={{fontSize:14,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
                      <div style={{fontSize:8,color:V.text3}}>{st.l}</div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Tab strip */}
              <div style={{display:"flex",gap:4,overflowX:"auto",paddingBottom:4}}>
                {TABS.map(t=>(
                  <button key={t.id} onClick={()=>setAuditTab(t.id)} style={{flexShrink:0,padding:"6px 12px",borderRadius:8,border:`1px solid ${auditTab===t.id?V.accent+"50":V.cardBorder}`,
                    background:auditTab===t.id?`${V.accent}10`:V.card,cursor:"pointer",
                    fontSize:10,fontWeight:auditTab===t.id?700:400,color:auditTab===t.id?V.accent:V.text3,fontFamily:V.font,whiteSpace:"nowrap"}}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>

              {/* Tab: Overview */}
              {auditTab==="overview"&&(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:8}}>Bio & Profile</div>
                    <div style={{display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr 1fr":"1fr 1fr",gap:6,fontSize:10,color:V.text3}}>
                      {[
                        ["Name",`${detail.profile?.first_name||""} ${detail.profile?.last_name||""}`.trim()||"—"],
                        ["DOB",detail.profile?.date_of_birth||"—"],
                        ["Sex",detail.profile?.sex||"—"],
                        ["State",detail.profile?.state||"—"],
                        ["Height",detail.profile?.height?`${detail.profile.height}"`:"—"],
                        ["Current Wt",detail.profile?.current_weight?`${detail.profile.current_weight} ${detail.profile?.units||"lbs"}`:"—"],
                        ["Target Wt",detail.profile?.target_weight?`${detail.profile.target_weight} ${detail.profile?.units||"lbs"}`:"—"],
                        ["Activity",detail.profile?.activity_level||"—"],
                        ["Friend Code",detail.profile?.friend_code||"—"],
                        ["Consented",detail.profile?.consented_at?.slice(0,10)||"—"],
                        ["Email",detail.profile?.email_verified?"Verified":"Unverified"],
                        ["PIN",(detail.profile?.has_pin??!!detail.profile?.account_pin)?(detail.profile?.pin_locked?"Set · LOCKED":"Set"):"None"],
                      ].map(([k,v])=>(
                        <div key={k} style={{padding:"4px 8px",borderRadius:6,background:V.card}}>
                          <div style={{fontSize:8,color:V.text3,marginBottom:1}}>{k}</div>
                          <div style={{fontSize:11,color:V.text,fontWeight:500}}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {detail.profile?.bio&&<div style={{marginTop:8,padding:8,borderRadius:8,background:V.card,fontSize:11,color:V.text2,fontStyle:"italic"}}>"{detail.profile.bio}"</div>}
                  </Card>
                  {detail.syncHistory?.length>0&&<Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Sync History ({detail.syncHistory.length})</div>
                    {detail.syncHistory.slice(0,8).map((l,i)=>(
                      <div key={i} style={{fontSize:9,color:V.text3,padding:"3px 0",borderBottom:`1px solid ${V.divider}`}}>
                        {l.synced_at?.slice(0,16)} · <span style={{color:V.accent,fontFamily:V.mono}}>{l.device_id?.slice(0,14)}</span> · {l.records_synced||"?"} records
                      </div>
                    ))}
                  </Card>}
                </div>
              )}

              {/* Tab: Workouts */}
              {auditTab==="workouts"&&(
                <Card style={{padding:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>All Workouts ({detail.workouts?.length||0})</div>
                  <div style={{maxHeight:400,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                    {(detail.workouts||[]).map((w,i)=>(
                      <div key={i} style={{padding:"8px 10px",borderRadius:8,background:V.card,border:`1px solid ${V.cardBorder}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:12,fontWeight:700,color:V.accent,fontFamily:V.mono}}>{w.date}</span>
                          <span style={{fontSize:10,color:V.text3}}>{(w.exercises||[]).length} exercises · {w.duration||"?"}min</span>
                        </div>
                        {(w.exercises||[]).length>0&&(
                          <div style={{marginTop:4,display:"flex",flexWrap:"wrap",gap:3}}>
                            {(w.exercises||[]).map((e,j)=>(
                              <span key={j} style={{fontSize:8,padding:"1px 6px",borderRadius:4,background:`${V.accent}12`,color:V.accent}}>
                                {e.exerciseId} {e.sets?.length?`(${e.sets.length}s)`:""}
                              </span>
                            ))}
                          </div>
                        )}
                        {w.notes&&<div style={{marginTop:4,fontSize:9,color:V.text3,fontStyle:"italic"}}>"{w.notes}"</div>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Tab: Nutrition */}
              {auditTab==="nutrition"&&(
                <Card style={{padding:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Nutrition Log ({detail.nutrition?.length||0} days)</div>
                  <div style={{maxHeight:400,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                    {(detail.nutrition||[]).map((n,i)=>(
                      <div key={i} style={{padding:"8px 10px",borderRadius:8,background:V.card,border:`1px solid ${V.cardBorder}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                          <span style={{fontSize:12,fontWeight:700,color:V.warn,fontFamily:V.mono}}>{n.date}</span>
                          <span style={{fontSize:10,color:V.text3}}>{n.calories||0} kcal</span>
                        </div>
                        <div style={{display:"flex",gap:8,fontSize:9,color:V.text3}}>
                          <span>P: {n.protein||0}g</span><span>C: {n.carbs||0}g</span><span>F: {n.fat||0}g</span>
                          {n.water&&<span>💧{n.water}</span>}
                          {n.meals?.length>0&&<span>{n.meals.length} meals</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Tab: Body */}
              {auditTab==="body"&&(
                <Card style={{padding:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Body Measurements ({detail.body?.length||0})</div>
                  <div style={{maxHeight:400,overflowY:"auto",display:"flex",flexDirection:"column",gap:3}}>
                    {(detail.body||[]).map((b,i)=>(
                      <div key={i} style={{padding:"7px 10px",borderRadius:8,background:V.card,fontSize:9,color:V.text3}}>
                        <span style={{color:V.purple,fontFamily:V.mono,fontWeight:700,fontSize:11}}>{b.date}</span>
                        {b.weight&&<span> · {b.weight}{detail.profile?.units||"lbs"}</span>}
                        {b.body_fat&&<span> · {b.body_fat}% BF</span>}
                        {b.chest&&<span> · C:{b.chest}"</span>}
                        {b.waist&&<span> · W:{b.waist}"</span>}
                        {b.hips&&<span> · H:{b.hips}"</span>}
                        {b.arms&&<span> · A:{b.arms}"</span>}
                        {b.legs&&<span> · L:{b.legs}"</span>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Tab: Check-ins */}
              {auditTab==="checkins"&&(
                <Card style={{padding:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Check-ins ({detail.checkins?.length||0})</div>
                  <div style={{maxHeight:400,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
                    {(detail.checkins||[]).map((c,i)=>(
                      <div key={i} style={{padding:"8px 10px",borderRadius:8,background:V.card}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:11,fontWeight:700,color:"#ec4899",fontFamily:V.mono}}>{c.date}</span>
                          <div style={{display:"flex",gap:8,fontSize:9,color:V.text3}}>
                            <span>😴 {c.sleep}h</span><span>💪 {c.soreness}/10</span><span>⚡ {c.energy}/10</span><span>😊 {c.motivation}/10</span>
                          </div>
                        </div>
                        {c.notes&&<div style={{fontSize:9,color:V.text2,fontStyle:"italic"}}>"{c.notes}"</div>}
                        {c.goals&&<div style={{fontSize:9,color:V.text3}}>Goals: {c.goals}</div>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Tab: Photos */}
              {auditTab==="photos"&&(
                <Card style={{padding:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>
                    Progress Photos ({detail.photos?.length||0}) <span style={{color:V.danger,fontSize:8}}>includes vault</span>
                  </div>
                  {photoNote&&<div style={{fontSize:10,color:V.text3,marginBottom:6}}>{photoNote}</div>}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                    {(detail.photos||[]).map((p,i)=>(
                      <div key={i} style={{borderRadius:8,overflow:"hidden",border:`1px solid ${p.is_private?V.purple+"40":V.cardBorder}`,aspectRatio:"1",position:"relative"}}>
                        {(photoUrls?.[p.photo_id]||p.public_url||p.photo_data)?
                          <img src={photoUrls?.[p.photo_id]||p.public_url||p.photo_data} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                          :<div style={{width:"100%",height:"100%",background:V.card,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:V.text3}}>{photoUrls===null?"…":"No preview"}</div>}
                        {p.is_private&&<div style={{position:"absolute",top:2,right:2,background:"rgba(168,85,247,0.8)",borderRadius:3,padding:"1px 4px",fontSize:7,color:"#fff"}}>🔒</div>}
                        <div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.65)",padding:"2px 4px",fontSize:7,color:"#fff"}}>{p.date?.slice(0,10)||p.created_at?.slice(0,10)}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Tab: Social */}
              {auditTab==="social"&&(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Friends ({detail.friends?.length||0})</div>
                    {(detail.friends||[]).map((f,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${V.divider}`,fontSize:10}}>
                        <span style={{color:V.text2}}>{f.name} <span style={{color:V.text3,fontSize:9}}>{f.email}</span></span>
                        <span style={{color:f.status==="accepted"?V.accent:V.warn,fontSize:9}}>{f.status}</span>
                      </div>
                    ))}
                  </Card>
                  {detail.activity?.length>0&&<Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Activity Events ({detail.activity.length})</div>
                    <div style={{maxHeight:300,overflowY:"auto"}}>
                      {detail.activity.map((a,i)=>(
                        <div key={i} style={{padding:"5px 0",borderBottom:`1px solid ${V.divider}`,fontSize:9}}>
                          <div style={{display:"flex",justifyContent:"space-between"}}>
                            <span style={{color:V.accent,fontWeight:700}}>{a.event_type}</span>
                            <span style={{color:V.text3}}>{a.created_at?.slice(0,16)}</span>
                          </div>
                          <div style={{color:V.text3,marginTop:2}}>{JSON.stringify(a.event_data||{}).slice(0,80)}</div>
                        </div>
                      ))}
                    </div>
                  </Card>}
                </div>
              )}

              {/* Tab: Gamification */}
              {auditTab==="gamification"&&(()=>{
                const g=gam||{};
                const bonus=g.xp_bonus?.total||0;
                const totalXP=(detail?.workouts?.length||0)*30+(detail?.nutrition?.length||0)*8+(detail?.checkins?.length||0)*5+(detail?.photos?.length||0)*15+bonus;
                const rank=[...IRON_RANKS].reverse().find(r=>totalXP>=r.xpNeeded)||IRON_RANKS[0];
                return(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <Card style={{padding:12,border:`1px solid ${rank.color}30`}}>
                      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                        <span style={{fontSize:32}}>{rank.icon}</span>
                        <div>
                          <div style={{fontSize:14,fontWeight:800,color:rank.color}}>{rank.name}</div>
                          <div style={{fontSize:10,color:V.text3}}>Level {rank.level} · {bonus.toLocaleString()} bonus XP</div>
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                        {[
                          {l:"Shields",v:g.streak_shields||0,c:"#22c55e"},
                          {l:"Badges",v:Object.keys(g.badge_dates||{}).length,c:"#f59e0b"},
                          {l:"Mission Days",v:Object.keys(g.missions_completed||{}).length,c:V.accent2},
                          {l:"War Wins",v:g.war_wins||0,c:"#f43f5e"},
                          {l:"War Streak",v:g.war_streak||0,c:V.danger},
                          {l:"Rivals",v:(g.rivals||[]).length,c:"#ec4899"},
                          {l:"Duels",v:(g.duels||[]).length,c:V.purple},
                          {l:"Comeback Used",v:g.comeback_used?g.comeback_used:"—",c:V.text3},
                        ].map(st=>(
                          <div key={st.l} style={{textAlign:"center",padding:6,borderRadius:8,background:V.card}}>
                            <div style={{fontSize:12,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
                            <div style={{fontSize:7,color:V.text3}}>{st.l}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                    {g.xp_bonus?.log?.length>0&&<Card style={{padding:12}}>
                      <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>XP Log ({g.xp_bonus.log.length})</div>
                      <div style={{maxHeight:250,overflowY:"auto"}}>
                        {g.xp_bonus.log.map((e,i)=>(
                          <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${V.divider}`,fontSize:9}}>
                            <span style={{color:V.text3}}>{e.date}</span>
                            <span style={{color:e.amount>=0?V.accent:V.danger,fontWeight:700,fontFamily:V.mono}}>{e.amount>=0?"+":""}{e.amount} XP</span>
                            <span style={{color:V.text3,flex:1,textAlign:"right",paddingLeft:8}}>{e.reason}</span>
                          </div>
                        ))}
                      </div>
                    </Card>}
                    {g.badge_dates&&Object.keys(g.badge_dates).length>0&&<Card style={{padding:12}}>
                      <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Earned Badges ({Object.keys(g.badge_dates).length})</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                        {Object.entries(g.badge_dates).map(([id,date])=>(
                          <div key={id} style={{padding:"3px 8px",borderRadius:6,background:`${V.warn}15`,border:`1px solid ${V.warn}30`,fontSize:9,color:V.warn}}>
                            🏅 {id} <span style={{color:V.text3}}>· {date}</span>
                          </div>
                        ))}
                      </div>
                    </Card>}
                  </div>
                );
              })()}

              {/* Tab: Activity Log */}
              {auditTab==="activity"&&(
                <Card style={{padding:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Full Activity Log ({detail.activity?.length||0} events)</div>
                  <div style={{maxHeight:500,overflowY:"auto"}}>
                    {(detail.activity||[]).map((a,i)=>(
                      <div key={i} style={{padding:"6px 8px",borderRadius:6,marginBottom:3,background:"rgba(255,255,255,0.015)"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:10,fontWeight:700,color:
                            a.event_type==="WorkoutLogged"?V.accent:
                            a.event_type==="PRHit"?"#f59e0b":
                            a.event_type==="DirectMessage"?"#06b6d4":
                            a.event_type.includes("Group")?"#ec4899":V.text3}}>{a.event_type}</span>
                          <span style={{fontSize:8,color:V.text3}}>{a.created_at?.slice(0,16)}</span>
                        </div>
                        <div style={{fontSize:8,color:V.text3,marginTop:2,fontFamily:V.mono}}>{JSON.stringify(a.event_data||{}).slice(0,120)}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Tab: Settings */}
              {auditTab==="settings"&&detail.settings&&(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:8}}>Goals & Schedule</div>
                    <div style={{display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr 1fr":"1fr 1fr",gap:6,fontSize:10}}>
                      {[
                        ["Calories",`${detail.settings.goals?.cal||"—"} kcal`],
                        ["Protein",`${detail.settings.goals?.protein||"—"}g`],
                        ["Carbs",`${detail.settings.goals?.carbs||"—"}g`],
                        ["Fat",`${detail.settings.goals?.fat||"—"}g`],
                        ["Workout Days",Object.values(detail.settings.schedule?.weekly||{}).join(", ")||"—"],
                        ["Phases",(detail.settings.phases||[]).length],
                        ["Injuries",(detail.settings.injuries||[]).length],
                        ["Supplements",(detail.settings.supplements||[]).length],
                      ].map(([k,v])=>(
                        <div key={k} style={{padding:"4px 8px",borderRadius:6,background:V.card}}>
                          <div style={{fontSize:8,color:V.text3}}>{k}</div>
                          <div style={{fontSize:11,color:V.text,fontWeight:500}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </Card>
                  {detail.settings.supplements?.length>0&&<Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Supplements</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {detail.settings.supplements.map((s2,i)=>(
                        <span key={i} style={{padding:"3px 8px",borderRadius:6,background:V.inputBg,fontSize:10,color:V.text2}}>{s2.name||s2}</span>
                      ))}
                    </div>
                  </Card>}
                  {detail.settings.injuries?.length>0&&<Card style={{padding:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:V.danger,marginBottom:6}}>Injuries</div>
                    {detail.settings.injuries.map((inj,i)=>(
                      <div key={i} style={{fontSize:10,color:V.text3,padding:"2px 0"}}>{typeof inj==="string"?inj:JSON.stringify(inj)}</div>
                    ))}
                  </Card>}
                </div>
              )}

              {/* Tab: Gym — the white-label view of this account (user_detail.org).
                  Exactly the block the admin panel's user detail renders; `null` means
                  the account is solo, which is an answer rather than a missing value. */}
              {auditTab==="gym"&&(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <OrgContextCard org={detail.org} email={selUser.email}/>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// ADMIN BUSINESS VALUE — analytics dashboard
// ═══════════════════════════════════════════════════════
// ─── Admin XP & Rank Manager ───
